import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, ArrowLeft, Folder, Package, AlertCircle, Pencil, Trash2, QrCode, ChevronRight, X, Loader2, Truck } from 'lucide-react';
import api from '../api/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const APP_URL = window.location.origin;

export default function AlmacenArea() {
  const { areaId } = useParams();
  const navigate = useNavigate();
  const [area, setArea] = useState(null);
  const [folders, setFolders] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: '', equipment_id: '', description: '', linkMode: 'equipment' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/warehouse/areas'),
      api.get(`/warehouse/folders?area_id=${areaId}`),
      api.get('/equipment'),
    ]).then(([a, f, e]) => {
      setArea(a.data.find(x => x.id === areaId) || null);
      setFolders(f.data);
      setEquipment(e.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [areaId]);

  const openCreate = () => {
    setForm({ name: '', equipment_id: '', description: '', linkMode: 'equipment' });
    setError(''); setModal({ mode: 'create' });
  };
  const openEdit = (folder) => {
    setForm({
      name: folder.name,
      equipment_id: folder.equipment_id || '',
      description: folder.description || '',
      linkMode: folder.equipment_id ? 'equipment' : 'free',
    });
    setError(''); setModal({ mode: 'edit', folder });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    const isEq = form.linkMode === 'equipment' && form.equipment_id;
    const eq = equipment.find(x => x.id === form.equipment_id);
    const payload = {
      area_id: areaId,
      name: isEq ? `${eq.code} · ${eq.name}` : form.name,
      equipment_id: isEq ? form.equipment_id : null,
      description: form.description,
    };
    try {
      if (modal.mode === 'edit') await api.put(`/warehouse/folders/${modal.folder.id}`, payload);
      else await api.post('/warehouse/folders', payload);
      setModal(null);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (folder) => {
    if (!window.confirm(`¿Eliminar la carpeta "${folder.name}"? Debe estar vacía.`)) return;
    try {
      await api.delete(`/warehouse/folders/${folder.id}`);
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'No se pudo eliminar la carpeta');
    }
  };

  return (
    <div className="p-6 md:p-8 page-enter">
      <div className="flex items-start gap-3 md:gap-4 mb-6">
        <button onClick={() => navigate('/inventario')} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 mt-1 flex-shrink-0" data-testid="back-to-areas">
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-400 mb-0.5">Almacén</p>
          <h1 className="text-2xl font-bold text-gray-900 truncate" style={{ fontFamily: 'Barlow, sans-serif' }}>
            {area?.name || 'Área'}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {folders.length} carpeta(s) · {area?.items_count ?? 0} referencias
            {area?.low_stock_count > 0 && <span className="text-amber-700 font-medium"> · {area.low_stock_count} bajo mínimos</span>}
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-medium px-3 md:px-4 py-2 rounded-lg flex-shrink-0"
          data-testid="add-folder-btn"
        >
          <Plus size={16} /> <span className="hidden sm:inline">Nueva Carpeta</span>
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#16a34a]" />
        </div>
      ) : folders.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center" data-testid="no-folders">
          <Folder size={28} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">Aún no hay carpetas en esta área.</p>
          <p className="text-xs text-gray-400 mt-1">Crea una por cada activo (ej. Camión IVECO FZF) o familia (ej. Separadores ópticos).</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {folders.map(folder => (
            <div
              key={folder.id}
              className="group bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-[#16a34a]/40 transition-all cursor-pointer overflow-hidden"
              onClick={() => navigate(`/almacen/carpeta/${folder.id}`)}
              data-testid={`folder-card-${folder.id}`}
            >
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${folder.equipment_id ? 'bg-blue-50' : 'bg-gray-100'}`}>
                      {folder.equipment_id
                        ? <Truck size={18} className="text-blue-600" />
                        : <Folder size={18} className="text-gray-500" />}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 text-sm truncate" style={{ fontFamily: 'Barlow, sans-serif' }}>{folder.name}</h3>
                      <p className="text-xs text-gray-400 truncate">
                        {folder.equipment_id ? 'Activo del GMAO' : 'Familia / agrupación'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                    <a
                      href={`${API_URL}/api/warehouse/folders/${folder.id}/qr?base_url=${encodeURIComponent(APP_URL)}`}
                      target="_blank" rel="noreferrer"
                      onClick={e => e.stopPropagation()}
                      className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
                      title="QR para la estantería"
                      data-testid={`folder-qr-${folder.id}`}
                    ><QrCode size={14} /></a>
                    <button onClick={e => { e.stopPropagation(); openEdit(folder); }}
                      className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                      title="Editar carpeta" data-testid={`edit-folder-${folder.id}`}><Pencil size={14} /></button>
                    <button onClick={e => { e.stopPropagation(); handleDelete(folder); }}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Eliminar carpeta" data-testid={`delete-folder-${folder.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-4 text-xs">
                  <span className="flex items-center gap-1.5 text-gray-600">
                    <Package size={13} className="text-gray-400" /> {folder.items_count} referencia(s)
                  </span>
                  {folder.low_stock_count > 0 && (
                    <span className="flex items-center gap-1 text-amber-700 font-medium">
                      <AlertCircle size={13} /> {folder.low_stock_count} bajo mínimos
                    </span>
                  )}
                </div>
              </div>
              <div className="px-5 py-2.5 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500">Ver repuestos</span>
                <ChevronRight size={14} className="text-gray-400 group-hover:text-[#16a34a] group-hover:translate-x-0.5 transition-all" />
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" data-testid="folder-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>
                {modal.mode === 'edit' ? 'Editar Carpeta' : 'Nueva Carpeta'}
              </h2>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-gray-600 p-1" data-testid="close-folder-modal"><X size={18} /></button>
            </div>
            <form onSubmit={handleSave} className="p-5 space-y-4">
              {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="folder-modal-error">{error}</div>}

              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setForm({ ...form, linkMode: 'equipment' })}
                  className={`px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${form.linkMode === 'equipment' ? 'bg-[#16a34a] text-white border-[#16a34a]' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                  data-testid="folder-mode-equipment">Activo del GMAO</button>
                <button type="button" onClick={() => setForm({ ...form, linkMode: 'free' })}
                  className={`px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${form.linkMode === 'free' ? 'bg-[#16a34a] text-white border-[#16a34a]' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                  data-testid="folder-mode-free">Familia / nombre libre</button>
              </div>

              {form.linkMode === 'equipment' ? (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Activo</label>
                  <select
                    value={form.equipment_id} required
                    onChange={e => setForm({ ...form, equipment_id: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white"
                    data-testid="folder-equipment-select"
                  >
                    <option value="">Selecciona un activo...</option>
                    {equipment.map(eq => (
                      <option key={eq.id} value={eq.id}>{eq.code} · {eq.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">La carpeta quedará enlazada a la ficha del activo.</p>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nombre de la carpeta</label>
                  <input
                    type="text" value={form.name} required autoFocus
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="Ej: Separadores ópticos"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none"
                    data-testid="folder-name-input"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Descripción</label>
                <textarea
                  value={form.description} rows={2}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none resize-none"
                  data-testid="folder-description-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={() => setModal(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-[#16a34a] text-white rounded-lg hover:bg-[#15803d]"
                  data-testid="save-folder-btn">
                  {saving && <Loader2 size={14} className="animate-spin" />}
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
